import { useDebounceFn, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type { DisputeStatusCount, Inbox } from '@stuut/api/models';

import { stuut } from '@/api';
import { useProfile } from '@/store/profile';

interface TaskStatus {
  all: number;
  archived: number;
  closed: number;
  complete: number;
  open: number;
}

export type CountType = 'disputes' | 'inbox' | 'tasks';

interface CountTypeConfig {
  enabled?: () => boolean;
  fetchData: () => Promise<any>;
  getCount: () => number;
  key: CountType;
}

const countTypes: CountType[] = ['disputes', 'inbox', 'tasks'];

const defaultSeen = (): Record<CountType, number> =>
  Object.fromEntries(countTypes.map((t) => [t, 0])) as Record<CountType, number>;

const defaultHasNew = (): Record<CountType, boolean> =>
  Object.fromEntries(countTypes.map((t) => [t, false])) as Record<CountType, boolean>;

const defaultLastAck = (): Record<CountType, string | null> =>
  Object.fromEntries(countTypes.map((t) => [t, null])) as Record<CountType, string | null>;

export const useStatusCounts = defineStore('statusCounts', () => {
  const profile = useProfile();

  const taskStatus = ref<TaskStatus | null>(null);
  const inboxStatus = ref<Inbox | null>(null);
  const disputeStatus = ref<DisputeStatusCount | null>(null);

  const registry: CountTypeConfig[] = [
    {
      key: 'tasks',
      fetchData: async () => {
        const response = await stuut.tasks.status();
        if (response?.data?.data) {
          taskStatus.value = response.data.data as TaskStatus;
        }
      },
      getCount: () => taskStatus.value?.open ?? 0,
    },
    {
      key: 'inbox',
      fetchData: async () => {
        inboxStatus.value = await stuut.inbox.getStatus();
      },
      getCount: () => inboxStatus.value?.unread ?? 0,
    },
    {
      key: 'disputes',
      enabled: () => Boolean(profile.isDisputesEnabled),
      fetchData: async () => {
        disputeStatus.value = await stuut.disputeStatusCounts.retrieve();
      },
      getCount: () => disputeStatus.value?.inactive ?? 0,
    },
  ];

  const configMap = Object.fromEntries(registry.map((c) => [c.key, c])) as Record<
    CountType,
    CountTypeConfig
  >;

  const loading = ref(
    Object.fromEntries(countTypes.map((t) => [t, false])) as Record<CountType, boolean>,
  );

  const lastFetched = ref(
    Object.fromEntries(countTypes.map((t) => [t, null])) as Record<CountType, number | null>,
  );

  // Scope persisted state by user+org. Without this, switching tenants lets
  // cross-tenant timestamp comparison in onPusherEvent silently drop real
  // events (lastCreatedAt <= lastAcknowledgedAt), breaking dedup monotonicity.
  const fingerprint = computed(() => {
    const userId = profile.profile?.id;
    const orgId = profile.profile?.organization?.id;
    return userId && orgId ? `${userId}:${orgId}` : null;
  });

  const isReady = computed(() => fingerprint.value !== null);

  const seenByFp = useStorage<Record<string, Record<CountType, number>>>(
    'stuut:status-counts:seen',
    {},
  );

  const hasNewByFp = useStorage<Record<string, Record<CountType, boolean>>>(
    'stuut:status-counts:has-new',
    {},
  );

  const lastAckByFp = useStorage<Record<string, Record<CountType, string | null>>>(
    'stuut:status-counts:last-acknowledged-at',
    {},
  );

  const fallbackSeen = ref(defaultSeen());
  const fallbackHasNew = ref(defaultHasNew());
  const fallbackLastAck = ref(defaultLastAck());

  const seenCounts = computed(() => {
    const fp = fingerprint.value;
    if (!fp) return fallbackSeen.value;
    if (!seenByFp.value[fp]) seenByFp.value[fp] = defaultSeen();
    return seenByFp.value[fp];
  });

  const hasNew = computed(() => {
    const fp = fingerprint.value;
    if (!fp) return fallbackHasNew.value;
    if (!hasNewByFp.value[fp]) hasNewByFp.value[fp] = defaultHasNew();
    return hasNewByFp.value[fp];
  });

  const lastAcknowledgedAt = computed(() => {
    const fp = fingerprint.value;
    if (!fp) return fallbackLastAck.value;
    if (!lastAckByFp.value[fp]) lastAckByFp.value[fp] = defaultLastAck();
    return lastAckByFp.value[fp];
  });

  const activeCountType = ref<CountType | null>(null);

  const pendingTimestamp = ref(
    Object.fromEntries(countTypes.map((t) => [t, null])) as Record<CountType, string | null>,
  );

  const isCacheFresh = (type: CountType, ttlMs = 60_000): boolean => {
    const ts = lastFetched.value[type];
    if (!ts) return false;
    return Date.now() - ts < ttlMs;
  };

  const fetchCounts = async (type: CountType, force = false) => {
    const config = configMap[type];
    if (config.enabled && !config.enabled()) return;
    if (!force && isCacheFresh(type)) return;
    if (loading.value[type]) return;

    try {
      loading.value[type] = true;
      await config.fetchData();
      lastFetched.value[type] = Date.now();
    } catch (err: any) {
      console.error(err.clientMessage ?? err.message ?? err);
    } finally {
      loading.value[type] = false;
    }
  };

  const fetchTaskCounts = (force = false) => fetchCounts('tasks', force);
  const fetchInboxCounts = (force = false) => fetchCounts('inbox', force);
  const fetchDisputeCounts = (force = false) => fetchCounts('disputes', force);

  const fetchAll = async () => {
    await Promise.all(countTypes.map((t) => fetchCounts(t, true)));
    syncSeenCounts();
  };

  const invalidate = (type?: CountType) => {
    if (type) {
      lastFetched.value[type] = null;
    } else {
      for (const t of countTypes) {
        lastFetched.value[t] = null;
      }
    }
  };

  const getCountForType = (type: CountType): number => {
    return configMap[type].getCount();
  };

  // seenCounts may drift while hasNew is true (e.g. count drops then rises back),
  // but markSeen always resets to the current count so it self-corrects.
  const syncSeenCounts = () => {
    if (!isReady.value) return;
    for (const type of countTypes) {
      if (lastFetched.value[type] === null) continue;
      const currentCount = getCountForType(type);
      if (hasNew.value[type] || currentCount > seenCounts.value[type]) {
        hasNew.value[type] = true;
      } else {
        seenCounts.value[type] = currentCount;
      }
    }
  };

  const executePusherFetch = async (type: CountType) => {
    const timestamp = pendingTimestamp.value[type];

    await fetchCounts(type, true);

    if (isReady.value && activeCountType.value !== type) {
      hasNew.value[type] = true;
    }

    if (timestamp && isReady.value) {
      lastAcknowledgedAt.value[type] = timestamp;
    }
    pendingTimestamp.value[type] = null;
  };

  // If a fetch ever completes before profile loads, reconcile once ready.
  watch(isReady, (ready) => {
    if (ready) syncSeenCounts();
  });

  const debouncedFetchMap = Object.fromEntries(
    countTypes.map((t) => [t, useDebounceFn(() => executePusherFetch(t), 2000)]),
  ) as unknown as Record<CountType, () => void>;

  const onPusherEvent = (type: CountType, lastCreatedAt?: string) => {
    if (lastCreatedAt && isReady.value && lastAcknowledgedAt.value[type]) {
      if (lastCreatedAt <= lastAcknowledgedAt.value[type]!) {
        return;
      }
    }

    if (lastCreatedAt) {
      pendingTimestamp.value[type] = lastCreatedAt;
    }

    debouncedFetchMap[type]();
  };

  const markSeen = (type: CountType) => {
    if (!isReady.value) return;
    seenCounts.value[type] = getCountForType(type);
    hasNew.value[type] = false;
  };

  const openTaskCount = computed(() => taskStatus.value?.open ?? 0);
  const inactiveDisputeCount = computed(() => disputeStatus.value?.inactive ?? 0);
  const unreadEmailCount = computed(() => inboxStatus.value?.unread ?? 0);
  const triageEmailCount = computed(() => inboxStatus.value?.triage ?? 0);

  return {
    activeCountType,
    disputeStatus,
    fetchAll,
    fetchDisputeCounts,
    fetchInboxCounts,
    fetchTaskCounts,
    hasNew,
    inactiveDisputeCount,
    inboxStatus,
    invalidate,
    loading,
    markSeen,
    onPusherEvent,
    openTaskCount,
    taskStatus,
    triageEmailCount,
    unreadEmailCount,
  };
});
